// Public electronic ballot demo (/electronic-ballot-demo).
//
// Everything here runs in the visitor's browser. Nothing is sent to the server
// and nothing is stored. The sealing is real Web Crypto with the same
// algorithms the live ballot uses (packs/election_ballot.js):
//   - a fresh AES-256-GCM key per ballot,
//   - the selections encrypted with it, with "election|submission" bound in as
//     additional authenticated data,
//   - the AES key wrapped with the election's RSA-OAEP (SHA-256) public key.
// For the demo the election key pair is generated here, so the page can also
// play the inspector and unseal the ballot at the end.

const CANDIDATES = ['Avery Chen', 'Jordan Patel', 'Morgan Reyes', 'Sam Okafor', 'Taylor Brooks'];
const VOTES = 3;
const ELECTION_ID = '4821';
const OTHER_ELECTION_ID = '5117';

// Forty-six fictional ballots already in the box. Directors: 3 votes each,
// 6 of them abstained from the director race (40 x 3 = 120 votes cast).
const SAMPLE_BALLOTS = 46;
const SAMPLE_DIRECTORS = [36, 31, 27, 16, 10];
const SAMPLE_WRITEIN = 0;
const SAMPLE_IRS = { For: 31, Against: 9, Abstain: 6 };

// Gentle scrolling. The browser's built-in smooth scroll has a fixed, quick
// pace; this eases in and out over 0.8–1.4 s depending on distance, honors the
// element's scroll-margin (so headings clear the fixed navigation), and stops
// as soon as the visitor scrolls or presses a key.
const prefersReducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
let ebdScrollFrame = null;

function cancelGentleScroll() {
  if (ebdScrollFrame) { cancelAnimationFrame(ebdScrollFrame); ebdScrollFrame = null; }
}
['wheel', 'touchstart', 'keydown'].forEach((type) => window.addEventListener(type, cancelGentleScroll, { passive: true }));

function gentleScrollTo(y) {
  cancelGentleScroll();
  const max = document.documentElement.scrollHeight - window.innerHeight;
  const target = Math.max(0, Math.min(y, max));
  const start = window.scrollY;
  const distance = target - start;
  if (Math.abs(distance) < 2) return;
  if (prefersReducedMotion()) { window.scrollTo(0, target); return; }
  const duration = Math.min(1400, Math.max(800, Math.abs(distance) * 0.6));
  const ease = (p) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2);
  const t0 = performance.now();
  const step = (now) => {
    const p = Math.min(1, (now - t0) / duration);
    window.scrollTo(0, start + distance * ease(p));
    ebdScrollFrame = p < 1 ? requestAnimationFrame(step) : null;
  };
  ebdScrollFrame = requestAnimationFrame(step);
}

// block: 'start' (top, less the scroll-margin), 'center', or 'nearest'
// (only as far as needed to bring the element fully into view).
function gentleScrollToElement(el, block = 'start') {
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const margin = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
  const navBottom = (document.getElementById('main-nav')?.getBoundingClientRect().bottom) || 0;
  let y;
  if (block === 'center') {
    y = window.scrollY + rect.top - (window.innerHeight - rect.height) / 2;
  } else if (block === 'nearest') {
    const topLimit = Math.max(navBottom, margin) + 8;
    if (rect.top < topLimit) y = window.scrollY + rect.top - topLimit;
    else if (rect.bottom > window.innerHeight - 16) {
      y = window.scrollY + rect.bottom - window.innerHeight + 16;
      if (rect.height > window.innerHeight - topLimit - 16) y = window.scrollY + rect.top - topLimit;
    } else return;
  } else {
    y = window.scrollY + rect.top - Math.max(margin, navBottom + 8);
  }
  gentleScrollTo(y);
}

function initElectronicBallotDemo() {
  const root = document.querySelector('[data-ebd-root]');
  // A property on the element, not a data attribute: a restored page snapshot
  // copies attributes but not properties or listeners, so a restored copy
  // initializes again instead of sitting there unresponsive.
  if (!root || root.__ebdInitialized) return;
  root.__ebdInitialized = true;

  const $ = (sel) => root.querySelector(sel);
  const $$ = (sel) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, reduceMotion ? 0 : ms));
  const announcer = $('#ebd-announcer');
  const announce = (text) => { announcer.textContent = ''; setTimeout(() => { announcer.textContent = text; }, 30); };

  $('#ebd-nojs').classList.add('hidden');
  const subtle = window.crypto && window.crypto.subtle;
  if (!subtle) {
    $('#ebd-nocrypto').classList.remove('hidden');
    return;
  }

  // ── State ───────────────────────────────────────────────────────────────
  const fresh = () => ({
    stage: 1,
    votes: CANDIDATES.map(() => 0),
    writeIn: '',       // a write-in candidate's name; takes one vote, as on the live ballot
    abstainDirectors: false,
    irs: null,
    sealed: null,      // { ct, iv, wrapped, aad, original }
    tampered: false,
    smuggled: false,
    pollsClosed: false,
    completed: false,
    unsealed: false,
  });
  let state = fresh();
  let electionKeys = null;
  // The election keys. A failed attempt clears itself, so every later call
  // tries again instead of reusing a rejected promise.
  let keysPromise = null;
  const getKeys = () => {
    if (electionKeys) return Promise.resolve(electionKeys);
    if (!keysPromise) {
      keysPromise = subtle.generateKey(
        { name: 'RSA-OAEP', modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]), hash: 'SHA-256' },
        true, ['encrypt', 'decrypt']
      ).then(
        (keys) => { electionKeys = keys; return keys; },
        (error) => { keysPromise = null; throw error; }
      );
    }
    return keysPromise;
  };
  getKeys().catch(() => {}); // start early; a failure here is retried on demand

  // ── Helpers ─────────────────────────────────────────────────────────────
  const hex = (bytes) => Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  const preview = (bytes, chars = 96) => {
    const h = hex(bytes);
    return h.length > chars ? `${h.slice(0, chars)}… (${bytes.length} bytes)` : `${h} (${bytes.length} bytes)`;
  };
  const used = () => (state.abstainDirectors ? VOTES : state.votes.reduce((a, b) => a + b, 0) + (state.writeIn ? 1 : 0));
  const timeNow = () => new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  function setRail(step, status) {
    const li = root.querySelector(`[data-rail-step="${step}"]`);
    if (!li) return;
    li.classList.toggle('bg-[#491363]', status === 'done');
    li.classList.toggle('bg-[#f3ecf7]', status === 'current');
    li.classList.toggle('bg-white', status === 'todo');
    li.querySelector('[data-rail-num]').classList.toggle('text-white', status === 'done');
    const label = li.querySelector('.text-gray-700');
    if (label) label.classList.toggle('text-white', status === 'done');
    li.querySelector('[data-rail-state]').textContent =
      status === 'done' ? ', done' : status === 'current' ? ', current step' : ', not started';
    const link = li.querySelector('a');
    if (status === 'current') link.setAttribute('aria-current', 'step'); else link.removeAttribute('aria-current');
  }

  function goToStage(n, { focus = true } = {}) {
    state.stage = n;
    for (let i = 1; i <= 5; i++) {
      setRail(i, i < n ? 'done' : i === n ? 'current' : 'todo');
      const section = $(`#ebd-stage-${i}`);
      section.dataset.locked = i > n ? 'true' : 'false';
    }
    if (focus) {
      const heading = $(`#ebd-h-${n}`);
      gentleScrollToElement(heading.closest('section') || heading, 'start');
      heading.focus({ preventScroll: true });
    }
  }

  // Bring an element into view only if it isn't already; used to follow the
  // demo as it moves, without yanking the page.
  function follow(el, block = 'nearest') {
    gentleScrollToElement(el, block);
  }

  const smoothTo = (el, block = 'start') => gentleScrollToElement(el, block);

  // In-page links scroll gently instead of jumping, and never reach
  // Turbolinks, whose same-page visit reloaded the page and cleared the demo.
  if (!window.__ebdAnchorsBound) {
    window.__ebdAnchorsBound = true;
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="#"][data-turbolinks="false"]');
      if (!link) return;
      const id = link.getAttribute('href').slice(1);
      const target = id && document.getElementById(id);
      if (!target) return;
      event.preventDefault();
      gentleScrollToElement(target, 'start');
      const stage = id.match(/^ebd-stage-(\d)$/);
      const heading = stage && document.getElementById(`ebd-h-${stage[1]}`);
      if (heading) heading.focus({ preventScroll: true });
      if (window.history && history.replaceState) history.replaceState(history.state, '', `#${id}`);
    }, true);
  }

  // Arrow cues: scroll gently to a target and put focus where the visitor acts next.
  root.addEventListener('click', (event) => {
    const cue = event.target.closest('[data-scroll-to]');
    if (!cue) return;
    const target = document.querySelector(cue.dataset.scrollTo);
    if (!target) return;
    smoothTo(target, cue.dataset.scrollBlock || 'start');
    const focusEl = cue.dataset.focus && document.querySelector(cue.dataset.focus);
    if (focusEl) focusEl.focus({ preventScroll: true });
    if (cue.id === 'ebd-cue-email') cue.classList.add('hidden');
  });

  // ── Civil Code checklist ────────────────────────────────────────────
  // Each requirement ticks once the visitor has seen it demonstrated.
  function markLaw(key) {
    const item = document.querySelector(`[data-law-item="${key}"]`);
    if (!item || item.dataset.done === 'true') return;
    item.dataset.done = 'true';
    item.querySelector('.ebd-law-mark').classList.remove('text-transparent');
    item.querySelector('[data-law-state]').textContent = 'illustrated';
    const count = document.querySelectorAll('[data-law-item][data-done="true"]').length;
    const counter = document.getElementById('ebd-law-count');
    if (counter) counter.textContent = count;
  }

  function resetLaw() {
    document.querySelectorAll('[data-law-item]').forEach((item) => {
      item.dataset.done = 'false';
      item.querySelector('.ebd-law-mark').classList.add('text-transparent');
      item.querySelector('[data-law-state]').textContent = 'not illustrated yet';
    });
    const counter = document.getElementById('ebd-law-count');
    if (counter) counter.textContent = '0';
  }

  // ── 01 The invitation ───────────────────────────────────────────────────
  // Weeks earlier, the pre-ballot notice lets the member check their device.
  const connectBtn = $('#ebd-connect');
  const connectIdle = 'The pre-ballot notice goes out more than 30 days before the voting deadline, so members can check their device in time.';
  connectBtn.disabled = false;
  connectBtn.addEventListener('click', async () => {
    connectBtn.disabled = true;
    connectBtn.classList.remove('ebd-pulse');
    connectBtn.textContent = 'Connecting…';
    await wait(700);
    connectBtn.textContent = 'Connected';
    $('#ebd-connect-result').textContent = 'Simulated. In a live election, logging in through this button confirms the member\u2019s device reaches the Condo Elects voting platform, weeks before the ballot arrives.';
    markLaw('connect');
    const cue = $('#ebd-cue-email');
    cue.classList.remove('hidden');
    announce('Connected. Your device reached the voting platform.');
    // On a phone the arrow appears below the screen; ease down so it's in view.
    await wait(350);
    follow(cue);
  });

  const openBtn = $('#ebd-open-link');
  openBtn.disabled = false;
  openBtn.addEventListener('click', async () => {
    openBtn.disabled = true;
    openBtn.classList.remove('ebd-pulse');
    openBtn.textContent = 'Checking your link…';
    markLaw('optout');
    markLaw('notice');
    const checks = $$('[data-link-check] .ebd-check');
    for (const check of checks) { await wait(550); check.classList.add('is-on'); }
    markLaw('auth');
    openBtn.textContent = 'Ballot opened';
    announce('Link verified. Your ballot is open.');
    enableBallot(true);
    await wait(700);
    goToStage(2);
  });

  // ── 02 The ballot ───────────────────────────────────────────────────────
  // Mirrors the live ballot (packs/election_ballot.js): each cumulative select
  // offers only the votes left; Abstain uses every vote and clears the
  // selects; a write-in takes one vote; clicking a row works its control; a
  // section's error shows in its card for 3 seconds; and the confirmation and
  // submit appear once anything is selected.
  const selects = $$('[data-ebd-cand]');
  const abstainBox = $('#ebd-abstain-directors');
  const irsRadios = $$('input[name="ebd-irs"]');
  let ballotEnabled = false;

  function enableBallot(on) {
    ballotEnabled = on;
    $$('#ebd-stage-2 select, #ebd-stage-2 input, #ebd-stage-2 button').forEach((el) => { el.disabled = !on; });
    if (on) updateDirectors();
    updateConfirmCue();
  }

  function setTotals(key, usedVotes, max) {
    const box = root.querySelector(`[data-ebd-totals="${key}"]`);
    box.querySelector('.bs-chip-used strong').textContent = usedVotes;
    box.querySelector('.bs-chip-unused strong').textContent = Math.max(0, max - usedVotes);
  }

  function showSectionError(key, message) {
    const el = root.querySelector(`[data-ebd-error="${key}"]`);
    el.textContent = message;
    el.style.display = 'block';
    clearTimeout(el._hideTimer);
    el._hideTimer = setTimeout(() => { el.style.display = 'none'; }, 3000);
  }

  function fillOptions(select, max, value) {
    select.innerHTML = '<option value="">0</option>';
    for (let n = 1; n <= max; n++) {
      const option = document.createElement('option');
      option.value = String(n);
      option.textContent = String(n);
      select.appendChild(option);
    }
    select.value = value;
  }

  function showWriteInInput(visible) {
    $('#ebd-wi-input-group').style.display = visible ? 'flex' : 'none';
  }

  function updateDirectors() {
    if (state.abstainDirectors) {
      state.votes = CANDIDATES.map(() => 0);
      if (state.writeIn) removeWriteIn({ update: false });
      selects.forEach((select) => fillOptions(select, VOTES, ''));
      showWriteInInput(false);
    } else {
      const remaining = Math.max(0, VOTES - used());
      selects.forEach((select, i) => fillOptions(select, remaining + state.votes[i], state.votes[i] ? String(state.votes[i]) : ''));
      if (!state.writeIn) showWriteInInput(remaining > 0);
    }
    setTotals('directors', used(), VOTES);
    updateReview();
  }

  selects.forEach((select, i) => {
    select.addEventListener('change', () => {
      state.votes[i] = parseInt(select.value || '0', 10);
      if (state.abstainDirectors) { state.abstainDirectors = false; abstainBox.checked = false; }
      updateDirectors();
    });
  });

  abstainBox.addEventListener('change', () => {
    state.abstainDirectors = abstainBox.checked;
    updateDirectors();
  });

  function addWriteIn() {
    const name = $('#ebd-writein').value.trim();
    if (!name) {
      showSectionError('directors', 'Please enter a name for the write-in candidate.');
      return;
    }
    if (state.abstainDirectors) { state.abstainDirectors = false; abstainBox.checked = false; }
    state.writeIn = name;
    $('#ebd-wi-display-text').textContent = `${name} (Write-In Candidate)`;
    showWriteInInput(false);
    $('#ebd-wi-display').style.display = 'flex';
    updateDirectors();
  }

  function removeWriteIn({ update = true } = {}) {
    state.writeIn = '';
    $('#ebd-writein').value = '';
    $('#ebd-wi-display-text').textContent = '';
    $('#ebd-wi-display').style.display = 'none';
    showWriteInInput(true);
    if (update) updateDirectors();
  }

  $('#ebd-wi-add').addEventListener('click', addWriteIn);
  $('#ebd-wi-remove').addEventListener('click', () => removeWriteIn());
  $('#ebd-writein').addEventListener('keypress', (event) => {
    if (event.key === 'Enter') { event.preventDefault(); addWriteIn(); }
  });

  irsRadios.forEach((radio) => {
    radio.addEventListener('change', () => {
      state.irs = radio.value;
      setTotals('irs', 1, 1);
      updateReview();
    });
  });

  // Clicking anywhere on a row works its control, and Space or Enter on a
  // focused row does the same.
  $$('[data-ebd-row]').forEach((row) => {
    const input = row.querySelector('input, select');
    row.addEventListener('click', (event) => {
      if (!ballotEnabled || event.target === input || event.target.closest('label')) return;
      if (input.tagName === 'SELECT') {
        input.focus();
      } else if (input.type === 'checkbox') {
        input.checked = !input.checked;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (input.type === 'radio') {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    row.addEventListener('keydown', (event) => {
      if (event.target !== row) return;
      if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); row.click(); }
    });
  });

  function reviewData() {
    const sections = [];
    const directors = [];
    if (state.abstainDirectors) {
      directors.push({ label: 'Abstain', votes: null, isAbstain: true });
    } else {
      CANDIDATES.forEach((name, i) => { if (state.votes[i] > 0) directors.push({ label: name, votes: state.votes[i] }); });
      if (state.writeIn) directors.push({ label: `${state.writeIn} (Write-In Candidate)`, votes: null });
    }
    if (directors.length) sections.push({ title: 'Election for Board of Directors', isCumulative: true, choices: directors });
    if (state.irs) sections.push({ title: 'IRS Resolution', isCumulative: false, choices: [{ label: state.irs, votes: null, isAbstain: state.irs === 'Abstain' }] });
    return sections;
  }

  function updateReview() {
    const container = $('#ebd-review');
    const data = reviewData();
    container.innerHTML = '';
    if (data.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'review-empty';
      const p = document.createElement('p');
      p.className = 'text-gray-500 text-center py-8';
      p.textContent = 'Make your selections above to see them reviewed here.';
      empty.appendChild(p);
      container.appendChild(empty);
    } else {
      const list = document.createElement('div');
      list.className = 'review-sections space-y-4';
      data.forEach((section) => {
        const box = document.createElement('div');
        box.className = 'rounded-xl border border-gray-200 bg-gray-50 p-4 shadow-sm review-section';
        const title = document.createElement('h5');
        title.className = 'text-lg font-semibold text-gray-900 mb-3 review-section-title';
        title.textContent = section.title;
        const choices = document.createElement('div');
        choices.className = 'space-y-2';
        section.choices.forEach((choice) => {
          const row = document.createElement('div');
          row.className = 'flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-white px-4 py-2 text-sm text-gray-700 shadow-sm review-choice';
          const label = document.createElement('span');
          label.className = `choice-label ${choice.isAbstain ? 'text-yellow-700' : 'text-gray-800'}`;
          label.textContent = choice.label;
          row.appendChild(label);
          if (section.isCumulative && choice.votes !== null) {
            const votes = document.createElement('span');
            votes.className = 'font-semibold text-indigo-600 vote-count';
            votes.textContent = `(${choice.votes} vote${choice.votes === 1 ? '' : 's'})`;
            row.appendChild(votes);
          }
          choices.appendChild(row);
        });
        box.append(title, choices);
        list.appendChild(box);
      });
      container.appendChild(list);
    }
    $('#ebd-confirm-container').classList.toggle('hidden', data.length === 0);
    $('#ebd-submit-container').classList.toggle('hidden', data.length === 0);
    updateConfirmCue();
  }

  // A floating guide while the ballot is filled in. It points to the next
  // unanswered section, and to the confirmation only once every section is
  // answered, so following it never runs into a validation error. It shows
  // only when that next place is below the screen.
  const confirmCue = $('#ebd-to-confirm');
  function nextBallotStop() {
    if (!state.abstainDirectors && used() === 0) {
      return { target: '#ebd-card-directors', focus: '#ebd-sel-0', block: 'start', label: 'Next: Board of Directors' };
    }
    if (!state.irs) {
      return { target: '#ebd-card-irs', focus: '#ebd-irs-for', block: 'start', label: 'Next: IRS Resolution' };
    }
    return { target: '#ebd-confirm-container', focus: '#ebd-confirm', block: 'center', label: 'Confirm and submit' };
  }
  function updateConfirmCue() {
    let show = state.stage === 2 && ballotEnabled;
    if (show) {
      const stop = nextBallotStop();
      const el = $(stop.target);
      show = !!el && !el.classList.contains('hidden') && el.getBoundingClientRect().top > window.innerHeight - 80;
      if (show) {
        confirmCue.dataset.scrollTo = stop.target;
        confirmCue.dataset.focus = stop.focus;
        confirmCue.dataset.scrollBlock = stop.block;
        confirmCue.querySelector('[data-cue-label]').textContent = stop.label;
      }
    }
    confirmCue.classList.toggle('hidden', !show);
    confirmCue.classList.toggle('inline-flex', show);
  }
  let cueTicking = false;
  const onScroll = () => {
    if (cueTicking) return;
    cueTicking = true;
    requestAnimationFrame(() => { cueTicking = false; updateConfirmCue(); });
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);

  function selections() {
    const directors = state.abstainDirectors
      ? 'abstain'
      : [
          ...CANDIDATES.map((name, i) => ({ candidate: name, votes: state.votes[i] })).filter((c) => c.votes > 0),
          ...(state.writeIn ? [{ write_in: `${state.writeIn} (Write-In Candidate)`, votes: 1 }] : []),
        ];
    return { election: 'Sample Shores HOA Annual Election 2026', sections: [
      { section: 'Board of Directors', selections: directors },
      { section: 'IRS Resolution', selection: state.irs },
    ] };
  }

  $('#ebd-submit').addEventListener('click', async () => {
    // The live ballot's checks, in its order: the confirmation first, then
    // each section, which shows its own message and stops at the first problem.
    const box = $('#ebd-ballot-errors');
    box.classList.add('hidden');
    box.textContent = '';
    const fail = (message) => { box.textContent = message; box.classList.remove('hidden'); box.focus(); };
    if (!$('#ebd-confirm').checked) { fail('Please confirm your ballot before submitting.'); return; }
    if (!state.abstainDirectors && used() === 0) {
      showSectionError('directors', 'Please allocate at least 1 vote or provide a write-in.');
      fail('Validation failed');
      return;
    }
    if (!state.irs) {
      showSectionError('irs', 'Please make a selection or provide a write-in.');
      fail('Validation failed');
      return;
    }
    enableBallot(false);
    $('#ebd-submit').textContent = 'Sealing your ballot…';
    goToStage(3);
    try {
      await seal();
    } catch (_) {
      // Nothing was sent; give the ballot back so the visitor can try again.
      state.sealed = null;
      $$('[data-seal-step]').forEach((c) => c.classList.remove('is-on'));
      $$('[data-seal-value]').forEach((c) => { c.textContent = ''; });
      $('#ebd-plaintext').textContent = 'Submit your ballot in step 02 to see it here.';
      $('#ebd-submit').textContent = 'Submit Ballot';
      enableBallot(true);
      goToStage(2);
      box.textContent = "Something went wrong sealing your ballot, so nothing was submitted. Your selections are still here. Please try again.";
      box.classList.remove('hidden');
      box.focus();
      announce('Sealing failed. Your ballot was not submitted. Please try again.');
    }
  });

  // ── 03 The seal ─────────────────────────────────────────────────────────
  async function seal() {
    const plaintext = JSON.stringify(selections(), null, 2);
    $('#ebd-plaintext').textContent = plaintext;
    const { publicKey } = await getKeys();

    const aesKey = await subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
    const rawKey = new Uint8Array(await subtle.exportKey('raw', aesKey));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const submission = crypto.randomUUID ? crypto.randomUUID() : hex(crypto.getRandomValues(new Uint8Array(16)));
    const aad = `${ELECTION_ID}|${submission}`;
    const ct = new Uint8Array(await subtle.encrypt(
      { name: 'AES-GCM', iv, additionalData: new TextEncoder().encode(aad) }, aesKey, new TextEncoder().encode(plaintext)
    ));
    const wrapped = new Uint8Array(await subtle.encrypt({ name: 'RSA-OAEP' }, publicKey, rawKey));
    state.sealed = { ct, original: ct.slice(), iv, wrapped, aad, submission };

    const reveal = async (id, value) => {
      await wait(850);
      root.querySelector(`[data-seal-value="${id}"]`).textContent = value;
      const step = root.querySelector(`[data-seal-step="${id}"]`);
      step.classList.add('is-on');
      follow(step);
    };
    await reveal('key', preview(rawKey, 64));
    await reveal('ct', preview(ct));
    await reveal('aad', `election ${ELECTION_ID} | submission ${submission}`);
    await reveal('wrapped', preview(wrapped));

    await wait(500);
    root.querySelector('[data-packet="ct"]').textContent = preview(ct, 120);
    root.querySelector('[data-packet="wrapped"]').textContent = preview(wrapped, 120);
    root.querySelector('[data-packet="iv"]').textContent = preview(iv, 24);
    $('#ebd-received').textContent = `Your electronic ballot was received at ${timeNow()}.`;
    $('#ebd-packet').classList.add('is-on');
    follow($('#ebd-packet'));
    markLaw('transmit');

    await wait(500);
    $('#ebd-receipt-panel').classList.add('is-on');
    follow($('#ebd-receipt-panel'));
    markLaw('receipt');
    await wait(500);
    $('#ebd-separation-panel').classList.add('is-on');
    $('#ebd-submit').textContent = 'Ballot submitted';
    announce('Your ballot was sealed and received.');

    $$('[data-challenge]').forEach((b) => { b.disabled = false; });
    $$('[data-count-action]').forEach((b) => { b.disabled = b.dataset.countAction !== 'close'; });
    highlightCount();
    // Stay on step 03 so the visitor can read it; they move on when ready.
    goToStage(4, { focus: false });
    await wait(400);
    const next = $('#ebd-next-3');
    next.classList.remove('hidden');
    follow(next);
    next.querySelector('button').focus({ preventScroll: true });
  }

  // "Next" buttons move to the next step's heading.
  root.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-next-stage]');
    if (!btn) return;
    goToStage(parseInt(btn.dataset.nextStage, 10));
  });

  // ── 04 The ballot box ───────────────────────────────────────────────────
  const result = $('#ebd-challenge-result');
  const verdict = (tone, title, body) => {
    const color = tone === 'blocked' ? 'text-[#491363]' : 'text-amber-700';
    result.innerHTML = '';
    const t = document.createElement('p'); t.className = `font-semibold ${color} mb-2`; t.textContent = title;
    result.appendChild(t);
    body.forEach((line) => {
      const p = document.createElement('p');
      if (line.hex) { p.className = 'ebd-hex text-[11px] text-gray-500 my-2'; p.textContent = line.hex; }
      else { p.textContent = line; }
      result.appendChild(p);
    });
  };

  root.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-challenge]');
    if (!btn || !state.sealed) return;
    const s = state.sealed;
    switch (btn.dataset.challenge) {
      case 'peek':
        verdict('blocked', 'Encrypted.', [
          'Ballot · received · sealed',
          { hex: preview(s.ct, 160) },
          'Ballots are encrypted before submission. The inspector opens them for the public count, and the system records who released the election key and when.',
        ]);
        markLaw('sealed');
        break;
      case 'twice':
        verdict('blocked', 'Rejected: SS-0142 has already voted.', [
          'Your unit is recorded as having voted, and the database allows only one such record per unit. A second ballot for the unit cannot be saved. That is enforced by the database itself, not by someone remembering to check.',
        ]);
        break;
      case 'tamper': {
        if (!state.tampered) {
          const i = Math.floor(s.ct.length / 2);
          s.ct[i] ^= 0x01;
          state.tampered = true;
        }
        $('#ebd-tamper-note').classList.remove('hidden');
        verdict('warn', 'Done. One bit of the sealed ballot is now different.', [
          { hex: preview(s.ct, 160) },
          'It looks just as unreadable as before. Nobody could tell by looking. Keep going to the count and see what happens.',
        ]);
        $('#ebd-undo-tamper').classList.remove('hidden');
        break;
      }
      case 'smuggle':
        state.smuggled = true;
        $('#ebd-tamper-note').classList.remove('hidden');
        verdict('warn', `Relabeled for election ${OTHER_ELECTION_ID}.`, [
          `The ballot was sealed to election ${ELECTION_ID}. You've filed it under election ${OTHER_ELECTION_ID}. Head to the count to see whether that works.`,
        ]);
        $('#ebd-undo-tamper').classList.remove('hidden');
        break;
    }
    if (state.stage < 5) goToStage(5, { focus: false });
    $('#ebd-next-4').classList.remove('hidden');
    follow(result);
  });

  $('#ebd-undo-tamper').addEventListener('click', () => {
    if (!state.sealed) return;
    state.sealed.ct = state.sealed.original.slice();
    state.tampered = false;
    state.smuggled = false;
    $('#ebd-undo-tamper').classList.add('hidden');
    $('#ebd-tamper-note').classList.add('hidden');
    result.textContent = 'Restored. The ballot is exactly as you sealed it, and it will be counted normally.';
  });

  // ── 05 The count ────────────────────────────────────────────────────────
  const log = (text) => {
    const li = document.createElement('li');
    li.textContent = `${timeNow()} · ${text}`;
    $('#ebd-log').appendChild(li);
  };

  function highlightCount() {
    const buttons = $$('[data-count-action]');
    buttons.forEach((b) => b.classList.remove('ebd-pulse', 'ebd-action-current'));
    const next = buttons.find((b) => !b.disabled && b.dataset.done !== 'true');
    if (next) next.classList.add('ebd-pulse', 'ebd-action-current');
  }

  function markCountDone(btn) {
    btn.dataset.done = 'true';
    btn.textContent = `\u2713 ${btn.dataset.label}`;
  }

  function resetCountButtons() {
    $$('[data-count-action]').forEach((b) => {
      b.dataset.done = 'false';
      b.textContent = b.dataset.label;
      b.classList.remove('ebd-pulse', 'ebd-action-current');
    });
  }

  root.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-count-action]');
    if (!btn || btn.disabled) return;
    const action = btn.dataset.countAction;
    btn.disabled = true;
    if (action !== 'unseal') markCountDone(btn);
    if (action === 'close') {
      state.pollsClosed = true;
      log('Polls closed at the open meeting. Ballot links stop working.');
      root.querySelector('[data-count-action="complete"]').disabled = false;
      highlightCount();
    } else if (action === 'complete') {
      state.completed = true;
      log('Election marked complete in the system. The key can now be unsealed.');
      root.querySelector('[data-count-action="unseal"]').disabled = false;
      highlightCount();
    } else if (action === 'unseal') {
      $$('[data-challenge]').forEach((b) => { b.disabled = true; });
      $('#ebd-undo-tamper').classList.add('hidden');
      // Unsealing is recorded once, even if opening the ballots needs a retry.
      if (!state.unsealed) log('Election unsealed by the Inspector of Elections. Release recorded in the election record.');
      state.unsealed = true;
      try {
        await count();
        markCountDone(btn);
        highlightCount();
      } catch (_) {
        $('#ebd-your-ballot').textContent = 'Something went wrong opening the ballots. Press Unseal and count to try again.';
        btn.disabled = false;
        highlightCount();
        announce('Opening the ballots failed. You can try again.');
      }
    }
  });

  async function count() {
    const s = state.sealed;
    const panel = $('#ebd-your-ballot');
    panel.textContent = 'Opening ballots…';
    await wait(700);

    // Only a broken seal counts as failed verification: AES-GCM rejects a
    // ballot whose bytes or election binding changed with an OperationError.
    // Any other error (keys, unwrapping, a transient failure) is thrown, so
    // the console offers a retry instead of discarding a good ballot.
    const { privateKey } = await getKeys();
    const rawKey = await subtle.decrypt({ name: 'RSA-OAEP' }, privateKey, s.wrapped);
    const aesKey = await subtle.importKey('raw', rawKey, { name: 'AES-GCM' }, false, ['decrypt']);
    const aad = state.smuggled ? `${OTHER_ELECTION_ID}|${s.submission}` : s.aad;
    let plain = null;
    try {
      plain = await subtle.decrypt({ name: 'AES-GCM', iv: s.iv, additionalData: new TextEncoder().encode(aad) }, aesKey, s.ct);
    } catch (error) {
      if (!error || error.name !== 'OperationError') throw error;
    }
    const opened = plain ? JSON.parse(new TextDecoder().decode(plain)) : null;

    panel.innerHTML = '';
    const head = document.createElement('p');
    if (opened) {
      head.className = 'font-semibold text-[#491363] mb-2';
      head.textContent = 'Your ballot opened cleanly and was counted.';
      panel.appendChild(head);
      const pre = document.createElement('pre');
      pre.className = 'ebd-hex text-xs text-gray-800 bg-[#faf8fc] border border-[#e4dce9] p-3 whitespace-pre-wrap';
      pre.textContent = JSON.stringify(opened.sections, null, 2);
      panel.appendChild(pre);
      log('1 electronic ballot verified.');
    } else {
      head.className = 'font-semibold text-red-700 mb-2';
      head.textContent = 'Your ballot failed verification.';
      panel.appendChild(head);
      const why = document.createElement('p');
      why.className = 'text-sm text-gray-600';
      why.textContent = state.smuggled
        ? `It was sealed to election ${ELECTION_ID}, so it will not open as part of election ${OTHER_ELECTION_ID}. It's flagged for the inspector instead of being counted as if nothing happened.`
        : 'One changed bit was enough to break the seal. The ballot is flagged for the inspector instead of being counted as if nothing happened.';
      panel.appendChild(why);
      log('1 electronic ballot failed verification and was flagged.');
    }
    announce(opened ? 'Your ballot was counted.' : 'Your ballot failed verification.');

    renderResults(opened);
    markLaw('sealed');
    markLaw('validate');
    markLaw('inspector');
    $('#ebd-restart').classList.remove('hidden');
    $('#ebd-to-checklist').classList.remove('hidden');
    const resultsPanel = $('#ebd-results-panel');
    gentleScrollToElement(resultsPanel, 'start');
    resultsPanel.focus({ preventScroll: true });
    setRail(5, 'done');
  }

  function renderResults(opened) {
    const directors = SAMPLE_DIRECTORS.slice();
    let writeIn = SAMPLE_WRITEIN;
    const irs = { ...SAMPLE_IRS };
    let ballots = SAMPLE_BALLOTS;
    let writeInLabel = 'Write-in';
    if (opened) {
      ballots += 1;
      const d = opened.sections[0].selections;
      if (Array.isArray(d)) {
        d.forEach((sel) => {
          if (sel.candidate) directors[CANDIDATES.indexOf(sel.candidate)] += sel.votes;
          if (sel.write_in) { writeIn += sel.votes; writeInLabel = sel.write_in; }
        });
      }
      irs[opened.sections[1].selection] += 1;
    }

    const rows = CANDIDATES.map((name, i) => ({ name, votes: directors[i] }));
    if (writeIn > 0) rows.push({ name: writeInLabel, votes: writeIn });
    rows.sort((a, b) => b.votes - a.votes);

    const wrap = $('#ebd-results');
    wrap.innerHTML = '';
    const summary = document.createElement('p');
    summary.className = 'text-xs text-gray-500';
    summary.textContent = opened
      ? `${ballots} ballots counted: ${SAMPLE_BALLOTS} sample ballots plus yours.`
      : `${ballots} ballots counted. Yours was flagged and isn't in these totals.`;
    wrap.appendChild(summary);

    const block = (title, items, winners) => {
      const max = Math.max(...items.map((r) => r.votes), 1);
      const section = document.createElement('div');
      const h = document.createElement('p');
      h.className = 'text-sm font-medium text-gray-900 mb-3';
      h.textContent = title;
      section.appendChild(h);
      const list = document.createElement('ul');
      list.className = 'space-y-2';
      items.forEach((item, idx) => {
        const li = document.createElement('li');
        li.className = 'text-sm';
        const row = document.createElement('div');
        row.className = 'flex justify-between gap-3 mb-1';
        const label = document.createElement('span');
        label.className = 'text-gray-700';
        label.textContent = item.name + (winners && idx < winners ? ' · Elected' : '');
        const val = document.createElement('span');
        val.className = 'tabular-nums text-gray-900 font-medium';
        val.textContent = item.votes;
        row.append(label, val);
        const track = document.createElement('div');
        track.className = 'h-2 bg-[#f3ecf7] rounded';
        track.setAttribute('aria-hidden', 'true');
        const bar = document.createElement('div');
        bar.className = `ebd-bar h-2 rounded ${winners && idx < winners ? 'bg-[#491363]' : 'bg-[#c49fd0]'}`;
        bar.style.width = '0%';
        track.appendChild(bar);
        li.append(row, track);
        list.appendChild(li);
        requestAnimationFrame(() => { bar.style.width = `${Math.round((item.votes / max) * 100)}%`; });
      });
      section.appendChild(list);
      wrap.appendChild(section);
    };

    block('Board of Directors (3 seats)', rows, 3);
    block('IRS Resolution', [
      { name: 'For', votes: irs.For }, { name: 'Against', votes: irs.Against }, { name: 'Abstain', votes: irs.Abstain },
    ], 0);

    const after = document.createElement('p');
    after.className = 'text-xs text-gray-500 leading-relaxed';
    after.textContent = 'In a live election, the inspector then issues the Certificate, the Tally Sheet, and the Quorum Report.';
    wrap.appendChild(after);
    wrap.classList.remove('hidden');
  }

  // ── Restart ─────────────────────────────────────────────────────────────
  $('#ebd-restart').addEventListener('click', () => {
    state = fresh();
    $$('[data-link-check] .ebd-check').forEach((c) => c.classList.remove('is-on'));
    $$('[data-seal-step]').forEach((c) => c.classList.remove('is-on'));
    $$('[data-seal-value]').forEach((c) => { c.textContent = ''; });
    $$('[data-packet]').forEach((c) => { c.textContent = '—'; });
    $('#ebd-packet').classList.remove('is-on');
    $('#ebd-received').textContent = '';
    $('#ebd-plaintext').textContent = 'Submit your ballot in step 02 to see it here.';
    abstainBox.checked = false;
    $('#ebd-confirm').checked = false;
    irsRadios.forEach((r) => { r.checked = false; });
    removeWriteIn({ update: false });
    selects.forEach((select) => fillOptions(select, VOTES, ''));
    setTotals('directors', 0, VOTES);
    setTotals('irs', 0, 1);
    $$('[data-ebd-error]').forEach((el) => { el.style.display = 'none'; });
    updateReview();
    $('#ebd-review').innerHTML = '<div class="rounded-2xl border border-dashed border-gray-300 bg-white/60 px-6 py-10 text-center text-sm text-gray-500 shadow-inner"><p>Make your selections above to see them listed here.</p></div>';
    $('#ebd-submit').textContent = 'Submit Ballot';
    enableBallot(false);
    $('#ebd-ballot-errors').classList.add('hidden');
    $$('[data-challenge]').forEach((b) => { b.disabled = true; });
    $$('[data-count-action]').forEach((b) => { b.disabled = true; });
    $('#ebd-undo-tamper').classList.add('hidden');
    result.textContent = 'Pick a challenge above.';
    $('#ebd-log').innerHTML = '';
    $('#ebd-your-ballot').textContent = 'The results appear here once the election is unsealed.';
    $('#ebd-results').classList.add('hidden');
    $('#ebd-restart').classList.add('hidden');
    $('#ebd-to-checklist').classList.add('hidden');
    $('#ebd-tamper-note').classList.add('hidden');
    $('#ebd-cue-email').classList.add('hidden');
    resetCountButtons();
    $('#ebd-next-3').classList.add('hidden');
    $('#ebd-next-4').classList.add('hidden');
    openBtn.disabled = false;
    openBtn.textContent = 'Open my secure ballot';
    openBtn.classList.add('ebd-pulse');
    connectBtn.disabled = false;
    connectBtn.textContent = 'Connect';
    connectBtn.classList.add('ebd-pulse');
    $('#ebd-connect-result').textContent = connectIdle;
    $('#ebd-receipt-panel').classList.remove('is-on');
    $('#ebd-separation-panel').classList.remove('is-on');
    resetLaw();
    goToStage(1);
  });

  goToStage(1, { focus: false });
}

// DOMContentLoaded fires on a cold load; turbolinks:load fires on every
// Turbolinks visit. The guard above keeps it to one initialization.
document.addEventListener('turbolinks:load', initElectronicBallotDemo);
document.addEventListener('DOMContentLoaded', initElectronicBallotDemo);
